import uiHelpers from '@/shared/ui_helpers';
import utility from '@/shared/utility';
import doRequest, { addLog } from '@/api/dssRequest';
import UserObject from '@/objects/UserObject';
import {
  isWeightedProduct,
  getUnitPrice,
  getRetailUnitPrice,
  getOverviewPrice,
  getRetailOverviewPrice,
  hasCapacity,
  getDisplayMinQuantity,
  getDisplayUnit,
  getDisplayStepSize,
} from '@/objects/Product';
import ProductOrderDialog from '@/components/event/eventDetails/productOrderComponents/ProductOrderDialog';
import HighlightingLabel from '@/components/generalComponents/HighlightingLabel';
import TrendingProductsButtons from '@/components/homepage/TrendingProductsButtons';

export default {
  mixins: [uiHelpers],
  components: {
    ProductOrderDialog,
    TrendingProductsButtons,
    HighlightingLabel,
  },
  data() {
    return {
      showProductDialog: false,
      selectedProduct: {},
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      products: [],
    };
  },
  computed: {
    title() {
      const pgInfo = this.$store.getters['Account/partnerGroupDetails'];

      if (
        pgInfo != null &&
        pgInfo.TrendingTitle != null &&
        pgInfo.TrendingTitle.trim() !== ''
      ) {
        return pgInfo.TrendingTitle.trim();
      }
      return this.$translate('TRENDING');
    },
    buttonWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return '120px';
        case 'sm':
          return '100px';
        case 'md':
          return '120px';
        case 'lg':
        case 'xl':
        default:
          return '140px';
      }
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
    partnerUserRelations() {
      return this.$store.getters['auth/partnerUserRelations'];
    },
    priceSuffix() {
      if (this.isProfUser) {
        return this.$translate('EXCL_VAT');
      }
      return '';
    },
  },
  mounted() {
    this.getTrendingProducts();
  },
  methods: {
    hasCapacity,
    getDisplayMinQuantity,
    getDisplayUnit,
    getDisplayStepSize,
    getTrendingProducts() {
      const payload = {};
      doRequest(
        (response) => {
          this.products = response.data.TrendingProducts;
          if (this.products.length > 5) {
            this.products = this.products.slice(0, 5);
          }
        },
        () => {},
        'getTrendingProducts',
        UserObject.getUserObjectId(),
        payload,
        'GET'
      );
    },
    roundThePrice(price) {
      return utility.roundPrice(price);
    },
    openProductDialog(product) {
      // set the product in the selectedProduct
      this.selectedProduct = { ...product };
      // open the dialog to show the product
      this.showProductDialog = true;
    },
    productSelected(product) {
      // Add user interaction to log
      addLog(this.userObjectId, product.Id, 'Product', 'Home', -1);
      // route to the details of the product
      const firstElement = 0;
      this.goToPage('ProductWithCategory', {
        event_id: product.EventId,
        category_id: product.Categories[firstElement].Id,
        product_id: product.Id,
      });
    },
    closeProductDialog() {
      this.showProductDialog = false;
      this.selectedProduct = {};
    },
    getProductPrice(product) {
      if (this.applyPersonalDiscount(product)) {
        return this.getProductPriceWithPersonalDiscount(product);
      }
      return this.getProductPriceWithoutPersonalDiscount(product);
    },
    getProductRetailPrice(product) {
      if (this.applyPersonalDiscount(product)) {
        return this.getProductRetailPriceWithPersonalDiscount(product);
      }
      return this.getProductRetailPriceWithoutPersonalDiscount(product);
    },
    getProductPriceWithPersonalDiscount(product) {
      let price = this.getProductPriceWithoutPersonalDiscount(product);
      if (price != null && this.applyPersonalDiscount(product)) {
        price *= 1 - this.personalDiscount(product) / 100;
      }
      return price;
    },
    getProductPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return product.PriceExclVat;
      }
      return product.Price;
    },
    getProductRetailPriceWithPersonalDiscount(product) {
      if (this.applyPersonalDiscount(product)) {
        return this.getProductPriceWithoutPersonalDiscount(product);
      }
      return null;
    },
    getProductRetailPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return utility.getPriceExclVAT(
          product.RetailPrice,
          product.VatPercentage
        );
      }
      return product.RetailPrice;
    },
    onDiscount(product) {
      // General discount check
      if (this.hasGeneralDiscount(product)) {
        return true;
      }

      // Personal discount check
      if (this.personalDiscount(product) > 0) {
        return true;
      }
      return false;
    },
    hasGeneralDiscount(product) {
      if (product != null && product.Discount) {
        return true;
      }
      return false;
    },
    personalDiscount(product) {
      const partnerId = product.PartnerId;
      const relations = this.partnerUserRelations[partnerId];
      if (
        relations != null &&
        relations.DiscountPercentage != null &&
        relations.DiscountPercentage > 0
      ) {
        return relations.DiscountPercentage;
      }
      return 0;
    },
    applyPersonalDiscount(product) {
      // Decides whether a personal discount should be used or not
      if (
        this.personalDiscount(product) > 0 &&
        !this.hasGeneralDiscount(product)
      ) {
        return true;
      }
      return false;
    },
    getShownRetailUnitPrice(product) {
      if (isWeightedProduct(product)) {
        if (this.hasGeneralDiscount(product)) {
          return getRetailOverviewPrice(product);
        }
        return getOverviewPrice(product);
      }

      if (this.hasGeneralDiscount(product)) {
        return getRetailUnitPrice(product);
      }
      return getUnitPrice(product);
    },
    getShownUnitPrice(product) {
      if (isWeightedProduct(product)) {
        return this.getPriceForPersonalDiscount(
          getOverviewPrice(product),
          product
        );
      }
      return this.getPriceForPersonalDiscount(getUnitPrice(product), product);
    },
    getPriceForPersonalDiscount(originalPrice, product) {
      let price = originalPrice;
      if (this.applyPersonalDiscount(product)) {
        price = utility.getDiscountPrice(
          originalPrice,
          this.personalDiscount(product)
        );
      }
      return price;
    },
    showPrice(product) {
      if (
        product.Type === 'Rental' &&
        this.getShownUnitPrice(product) < 0.005
      ) {
        return false;
      }
      return true;
    },
    isWeightedProduct,
  },
};
